import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Send, RefreshCw, CheckCircle2, AlertTriangle, Clock, Loader2, Radio, RotateCcw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TIERS } from "@/lib/tiers";

const WEBHOOK_BY_ACCESS = {
  public: "PUBLIC",
  member: "MEMBER",
  elite_cultist: "ELITE",
  hardcore_cultist: "HARDCORE",
  god_tier_supporter: "HARDCORE",
};

function accessLabel(a) {
  return TIERS[a]?.label || a;
}

function StatusBadge({ status }) {
  if (status === "synced")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-900/40 text-emerald-300 border border-emerald-500/40">
        <CheckCircle2 className="w-3 h-3" /> Synced
      </span>
    );
  if (status === "failed")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-900/40 text-rose-300 border border-rose-500/40">
        <AlertTriangle className="w-3 h-3" /> Failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-zinc-800 text-zinc-400 border border-white/10">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

function StatCard({ label, value, tone }) {
  const tones = {
    total: "text-white",
    synced: "text-emerald-300",
    failed: "text-rose-300",
    pending: "text-zinc-300",
  };
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`text-2xl font-black ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function AdminDiscordSync() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [retrying, setRetrying] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/discord/sync/status");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load sync status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyResult = (resp) => {
    if (resp?.status) setData(resp.status);
    if (resp?.result) setLastRun(resp.result);
  };

  const runSync = async () => {
    if (busy) return;
    setBusy(true);
    setLastRun(null);
    try {
      const { data } = await api.post("/admin/discord/sync/run");
      applyResult(data);
      const r = data.result || {};
      toast.success(`Sync done — ${r.sent || 0} sent, ${r.failed || 0} failed, ${r.skipped_already_synced || 0} skipped`);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("A sync is already running. Please wait.");
        load();
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = async () => {
    if (busy) return;
    setBusy(true);
    setLastRun(null);
    try {
      const { data } = await api.post("/admin/discord/sync/retry-failed");
      applyResult(data);
      const r = data.result || {};
      toast.success(`Retry done — ${r.sent || 0} synced, ${r.failed || 0} still failing`);
    } catch (e) {
      if (e.response?.status === 409) toast.error("A sync is already running. Please wait.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  const retryOne = async (postId) => {
    if (busy || retrying.has(postId)) return;
    setRetrying((s) => new Set(s).add(postId));
    try {
      const { data } = await api.post(`/admin/discord/sync/retry/${postId}`);
      applyResult(data);
      const one = (data.result?.results || []).find((x) => x.post_id === postId);
      if (one?.status === "synced") toast.success("Post synced to Discord");
      else toast.error(one?.error || "Still failing");
    } catch (e) {
      if (e.response?.status === 409) toast.error("A sync is already running. Please wait.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Retry failed");
    } finally {
      setRetrying((s) => {
        const n = new Set(s);
        n.delete(postId);
        return n;
      });
    }
  };

  const forceAll = async () => {
    if (busy) return;
    if (!window.confirm(
      "Force Resync will RE-SEND every published post to Discord — including posts already marked Synced.\n\n" +
      "Use this to restore Discord messages you deleted manually. Continue?"
    )) return;
    setBusy(true);
    setLastRun(null);
    try {
      const { data } = await api.post("/admin/discord/sync/force");
      applyResult(data);
      const r = data.result || {};
      toast.success(`Force resync done — ${r.sent || 0} re-sent, ${r.failed || 0} failed`);
    } catch (e) {
      if (e.response?.status === 409) { toast.error("A sync is already running. Please wait."); load(); }
      else toast.error(formatApiError(e.response?.data?.detail) || "Force resync failed");
    } finally {
      setBusy(false);
    }
  };

  const forceOne = async (postId) => {
    if (busy || retrying.has(postId)) return;
    setRetrying((s) => new Set(s).add(postId));
    try {
      const { data } = await api.post(`/admin/discord/sync/force/${postId}`);
      applyResult(data);
      const one = (data.result?.results || []).find((x) => x.post_id === postId);
      if (one?.status === "synced") toast.success("Post re-sent to Discord");
      else toast.error(one?.error || "Resend failed");
    } catch (e) {
      if (e.response?.status === 409) toast.error("A sync is already running. Please wait.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Resend failed");
    } finally {
      setRetrying((s) => {
        const n = new Set(s);
        n.delete(postId);
        return n;
      });
    }
  };

  const posts = data?.posts || [];
  const hasFailed = (data?.failed || 0) > 0;
  const hasSynced = (data?.synced || 0) > 0;

  return (
    <div className="space-y-6" data-testid="admin-discord-sync">
      <div className="flex items-center gap-3">
        <Radio className="w-5 h-5 text-indigo-400" />
        <div>
          <div className="font-mystic text-indigo-400 text-sm">Announcements</div>
          <h2 className="font-display text-2xl font-black text-white">Discord Content Sync</h2>
        </div>
      </div>

      <p className="text-sm text-zinc-400 max-w-3xl">
        Announce your <span className="text-white font-semibold">published TL Game posts</span> to the Discord channel
        that matches each post&apos;s Access Level. New posts are announced automatically when published — use this page to{" "}
        <span className="text-white font-semibold">backfill older posts</span>. Already-synced posts are skipped, so it&apos;s
        safe to click multiple times.
      </p>

      {loading ? (
        <div className="glass rounded-xl p-10 flex items-center justify-center text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Published" value={data?.total ?? 0} tone="total" />
            <StatCard label="Synced" value={data?.synced ?? 0} tone="synced" />
            <StatCard label="Failed" value={data?.failed ?? 0} tone="failed" />
            <StatCard label="Pending" value={data?.pending ?? 0} tone="pending" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={runSync}
              disabled={busy}
              data-testid="btn-sync-existing"
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {busy ? "Syncing…" : "Sync Existing Posts"}
            </Button>
            <Button
              onClick={retryFailed}
              disabled={busy || !hasFailed}
              variant="outline"
              data-testid="btn-retry-failed"
              className="border-white/15 text-zinc-200 hover:bg-white/5"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Retry Failed
            </Button>
            <Button
              onClick={forceAll}
              disabled={busy || !hasSynced}
              variant="outline"
              data-testid="btn-force-resync"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              title="Re-send every published post, including ones already synced. Use to restore deleted Discord messages."
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Force Resync All
            </Button>
            <Button
              onClick={load}
              disabled={busy}
              variant="ghost"
              className="text-zinc-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          <p className="text-[11px] text-amber-300/70 -mt-2">
            Tip: If you manually deleted a message in Discord, use <span className="font-semibold">Force Resync All</span>{" "}
            (or a row&apos;s <span className="font-semibold">Resend</span>) to post it again. Normal &quot;Sync Existing Posts&quot; skips already-synced posts.
          </p>

          {lastRun && (
            <div className="glass rounded-xl p-4 text-sm text-zinc-300" data-testid="sync-result">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-emerald-300">✓ {lastRun.sent ?? 0} synced</span>
                <span className="text-rose-300">⚠ {lastRun.failed ?? 0} failed</span>
                <span className="text-zinc-400">↷ {lastRun.skipped_already_synced ?? 0} already synced (skipped)</span>
              </div>
            </div>
          )}

          <div className="glass rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold text-white">
              Published Posts ({posts.length})
            </div>
            {posts.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No published posts found.</div>
            ) : (
              <div className="divide-y divide-white/5">
                {posts.map((p) => (
                  <div
                    key={p.post_id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]"
                    data-testid={`sync-row-${p.post_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{p.title || "Untitled"}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap items-center gap-2">
                        <span className="text-zinc-400">{accessLabel(p.access_level)}</span>
                        <span className="text-zinc-600">→ DISCORD_WEBHOOK_{WEBHOOK_BY_ACCESS[p.access_level] || "PUBLIC"}</span>
                        {p.last_sync_at && (
                          <span className="text-zinc-600">· {new Date(p.last_sync_at).toLocaleString()}</span>
                        )}
                      </div>
                      {p.error && p.status === "failed" && (
                        <div className="text-[11px] text-rose-400/80 mt-0.5 truncate">{p.error}</div>
                      )}
                    </div>
                    <StatusBadge status={p.status} />
                    {p.status !== "synced" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || retrying.has(p.post_id)}
                        onClick={() => retryOne(p.post_id)}
                        data-testid={`btn-retry-${p.post_id}`}
                        className="border-white/15 text-zinc-200 hover:bg-white/5 h-7 px-2 text-xs"
                      >
                        {retrying.has(p.post_id) ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3 h-3 mr-1" /> Send
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || retrying.has(p.post_id)}
                        onClick={() => forceOne(p.post_id)}
                        data-testid={`btn-force-${p.post_id}`}
                        title="Re-send this post to Discord (restore a deleted message)"
                        className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-7 px-2 text-xs"
                      >
                        {retrying.has(p.post_id) ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="w-3 h-3 mr-1" /> Resend
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
